import { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminCursoService } from '../../shared/services';
import { Save, X, ArrowLeft } from 'lucide-react';

export function AdminCursoFormPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const queryClient = useQueryClient();
  const isEditing = !!id;

  const [formData, setFormData] = useState({
    titulo: '',
    slug: '',
    descricao: '',
    categoria: '',
    ativo: true,
    capaUrl: '',
    duracao: '',
    nivel: 'iniciante' as 'iniciante' | 'intermediario' | 'avancado',
    instrutorId: '',
  });

  const { data: curso } = useQuery({
    queryKey: ['admin-curso', id],
    queryFn: () => adminCursoService.getCurso(id!),
    enabled: isEditing,
  });

  useEffect(() => {
    if (curso) {
      setFormData({
        titulo: curso.titulo,
        slug: curso.slug,
        descricao: curso.descricao,
        categoria: curso.categoria,
        ativo: curso.ativo,
        capaUrl: curso.capaUrl || '',
        duracao: curso.duracao || '',
        nivel: curso.nivel || 'iniciante',
        instrutorId: curso.instrutor || '',
      });
    }
  }, [curso]);

  const createMutation = useMutation({
    mutationFn: (data: typeof formData) => adminCursoService.createCurso(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-cursos'] });
      navigate('/admin/cursos');
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: typeof formData) => adminCursoService.updateCurso(id!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-cursos'] });
      queryClient.invalidateQueries({ queryKey: ['admin-curso', id] });
      navigate('/admin/cursos');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (isEditing) {
      updateMutation.mutate(formData);
    } else {
      createMutation.mutate(formData);
    }
  };

  const handleTituloChange = (titulo: string) => {
    setFormData(prev => ({
      ...prev,
      titulo,
      // Auto-gerar slug se não estiver editando
      slug: isEditing ? prev.slug : generateSlug(titulo),
    }));
  };

  const generateSlug = (text: string): string => {
    return text
      .toLowerCase()
      .normalize('NFD') // Decompor caracteres acentuados
      .replace(/[\u0300-\u036f]/g, '') // Remover acentos
      .replace(/[^a-z0-9]+/g, '-') // Substituir não-alfanuméricos por hífen
      .replace(/(^-|-$)/g, ''); // Remover hífens do início/fim
  };

  const handleGerarSlug = () => {
    setFormData(prev => ({
      ...prev,
      slug: generateSlug(prev.titulo),
    }));
  };

  const categorias = [
    'Financeiro',
    'Fiscal',
    'Recursos Humanos',
    'Vendas',
    'Compras',
    'Estoque',
    'Produção',
    'Controladoria',
    'Logística',
    'Outro',
  ];

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-8">
        <div className="mb-6">
          <Link
            to="/admin/cursos"
            className="inline-flex items-center text-gray-600 hover:text-gray-900 mb-4"
          >
            <ArrowLeft size={20} className="mr-2" />
            Voltar para cursos
          </Link>
          <h1 className="text-3xl font-bold text-gray-900">
            {isEditing ? 'Editar Curso' : 'Novo Curso'}
          </h1>
        </div>

        <form onSubmit={handleSubmit} className="card">
          <div className="space-y-6">
            {/* Título */}
            <div>
              <label htmlFor="titulo" className="block text-sm font-medium text-gray-700 mb-2">
                Título do Curso *
              </label>
              <input
                type="text"
                id="titulo"
                value={formData.titulo}
                onChange={(e) => handleTituloChange(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                required
              />
            </div>

            {/* Slug */}
            <div>
              <label htmlFor="slug" className="block text-sm font-medium text-gray-700 mb-2">
                Slug (URL) *
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  id="slug"
                  value={formData.slug}
                  onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  required
                  pattern="[a-z0-9\-]+"
                  title="Apenas letras minúsculas, números e hífens"
                />
                <button
                  type="button"
                  onClick={handleGerarSlug}
                  className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors font-medium whitespace-nowrap"
                  title="Gerar slug a partir do título"
                >
                  Gerar Slug
                </button>
              </div>
              <p className="mt-1 text-sm text-gray-500">
                URL amigável (ex: fundamentos-modulo-financeiro)
              </p>
            </div>

            {/* Descrição */}
            <div>
              <label htmlFor="descricao" className="block text-sm font-medium text-gray-700 mb-2">
                Descrição *
              </label>
              <textarea
                id="descricao"
                value={formData.descricao}
                onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
                rows={4}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                required
              />
            </div>

            {/* Grid de 2 colunas */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Categoria */}
              <div>
                <label htmlFor="categoria" className="block text-sm font-medium text-gray-700 mb-2">
                  Categoria *
                </label>
                <select
                  id="categoria"
                  value={formData.categoria}
                  onChange={(e) => setFormData({ ...formData, categoria: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  required
                >
                  <option value="">Selecione uma categoria</option>
                  {categorias.map((cat) => (
                    <option key={cat} value={cat}>
                      {cat}
                    </option>
                  ))}
                </select>
              </div>

              {/* Nível */}
              <div>
                <label htmlFor="nivel" className="block text-sm font-medium text-gray-700 mb-2">
                  Nível *
                </label>
                <select
                  id="nivel"
                  value={formData.nivel}
                  onChange={(e) => setFormData({ ...formData, nivel: e.target.value as 'iniciante' | 'intermediario' | 'avancado' })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  required
                >
                  <option value="iniciante">Iniciante</option>
                  <option value="intermediario">Intermediário</option>
                  <option value="avancado">Avançado</option>
                </select>
              </div>

              {/* Duração */}
              <div>
                <label htmlFor="duracao" className="block text-sm font-medium text-gray-700 mb-2">
                  Duração Estimada
                </label>
                <input
                  type="text"
                  id="duracao"
                  value={formData.duracao}
                  onChange={(e) => setFormData({ ...formData, duracao: e.target.value })}
                  placeholder="Ex: 20 horas"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>

              {/* Instrutor ID */}
              <div>
                <label htmlFor="instrutorId" className="block text-sm font-medium text-gray-700 mb-2">
                  ID do Instrutor
                </label>
                <input
                  type="text"
                  id="instrutorId"
                  value={formData.instrutorId}
                  onChange={(e) => setFormData({ ...formData, instrutorId: e.target.value })}
                  placeholder="ID do instrutor"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* URL da Capa */}
            <div>
              <label htmlFor="capaUrl" className="block text-sm font-medium text-gray-700 mb-2">
                URL da Imagem de Capa
              </label>
              <input
                type="url"
                id="capaUrl"
                value={formData.capaUrl}
                onChange={(e) => setFormData({ ...formData, capaUrl: e.target.value })}
                placeholder="https://exemplo.com/imagem.jpg"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
              {formData.capaUrl && (
                <div className="mt-3">
                  <img
                    src={formData.capaUrl}
                    alt="Preview"
                    className="w-full h-48 object-cover rounded-lg"
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                    }}
                  />
                </div>
              )}
            </div>

            {/* Status */}
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="ativo"
                checked={formData.ativo}
                onChange={(e) => setFormData({ ...formData, ativo: e.target.checked })}
                className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
              />
              <label htmlFor="ativo" className="text-sm font-medium text-gray-700">
                Curso ativo e visível no catálogo
              </label>
            </div>
          </div>

          {/* Botões de ação */}
          <div className="flex gap-3 mt-8 pt-6 border-t border-gray-200">
            <button
              type="submit"
              disabled={createMutation.isPending || updateMutation.isPending}
              className="btn-primary flex items-center gap-2"
            >
              <Save size={20} />
              {createMutation.isPending || updateMutation.isPending
                ? 'Salvando...'
                : isEditing
                ? 'Salvar Alterações'
                : 'Criar Curso'}
            </button>
            <Link to="/admin/cursos" className="btn-secondary flex items-center gap-2">
              <X size={20} />
              Cancelar
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
